import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["action", "className", "onKeyDown", "onSubmit"];
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef } from 'react';
import devLogger from 'react-utils/devLogger';
import invariant from 'react-utils/invariant';
import SyntheticEvent from '../core/SyntheticEvent';
import { preventDefaultHandler } from '../utils/DomEvents';
import { mergeRefs } from '../utils/mergeRefs';
import { jsx as _jsx } from "react/jsx-runtime";
let hasPingedNewRelic = false;
const UIForm = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      action,
      className,
      onKeyDown,
      onSubmit
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const formRef = useRef(null);
  useEffect(() => {
    if (!formRef.current) return;
    const childForms = formRef.current.querySelectorAll('form');
    const nestedFormsExist = childForms.length > 0;
    if (nestedFormsExist) {
      if (window.newrelic && !hasPingedNewRelic) {
        window.newrelic.addPageAction('componentWithNestedForm', {
          componentName: 'UIForm'
        });
        hasPingedNewRelic = true;
      }
      if (process.env.NODE_ENV !== 'production') {
        devLogger.warn({
          message: `A \`<form />\` was rendered within a \`UIForm\`. Please refactor.`,
          url: 'https://git.hubteam.com/HubSpot/UIComponents/issues/9507',
          key: 'UIForm-nested'
        });
        invariant(nestedFormsExist, 'A form cannot be nested in a form.');
      }
    }
  }, [formRef]);
  return (
    /*#__PURE__*/
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    _jsx("form", Object.assign({}, rest, {
      ref: mergeRefs([formRef, ref]),
      className: classNames('private-form', className),
      action: action,
      onKeyDown: evt => {
        // Submit on Cmd+Enter or Ctrl+Enter (avoiding double-submit from an <input>: #4578)
        const inputFocused = document.activeElement instanceof HTMLInputElement;
        if (evt.key === 'Enter') {
          if (evt.metaKey || evt.ctrlKey && !inputFocused) {
            // @ts-expect-error 'SyntheticEventClass<null>' is not assignable to parameter of type 'FormEvent<HTMLFormElement>'
            if (onSubmit) onSubmit(SyntheticEvent(null));
          }
        }
        if (onKeyDown) onKeyDown(evt);
      },
      onSubmit: !action && !onSubmit ? preventDefaultHandler : onSubmit
    }))
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
  );
});
UIForm.propTypes = {
  action: PropTypes.string,
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func
};
UIForm.displayName = 'UIForm';
export default UIForm;